/**
 * Camada das estações meteorológicas no mapa.
 *
 * ~160 pontos fixos: a camada vetorial comum do OpenLayers dá conta, e o
 * estilo nativo entra na composição da exportação.
 *
 * O símbolo (ver `docs/meteo.md`):
 * - **quadrado** para as estações do INMET e **losango** para os aeródromos
 *   (METAR) — nenhum dos dois se confunde com o círculo das sedes e dos rios,
 *   a gota dos pluviômetros ou a chama dos focos;
 * - pintado pela **escala da variável escolhida** no painel, com o **valor
 *   escrito ao lado** na mesma cor ("31°", "45%");
 * - a **seta do vento** sai do símbolo e aponta para ONDE o vento vai (é o que
 *   se lê de relance: a fumaça segue a seta), com o comprimento pela
 *   velocidade. Traço fino e vazado, para não virar o triângulo cheio das
 *   embarcações.
 *
 * Estação sem observação no quadro fica vazada e tracejada, sem número — o
 * mesmo `SEM_DADO` dos pluviômetros: calada não é zero. Observação com mais
 * de 1 h (o INMET publica a hora cheia uma hora depois) fica esmaecida.
 *
 * Os rótulos passam pelo `declutter`: com a Amazônia inteira na tela, os
 * números que colidiriam somem, e voltam ao aproximar. O símbolo e a seta
 * nunca somem — são obstáculos para os rótulos, não candidatos.
 *
 * A cada quadro do loop a leitura é refeita (`leitura.ts`): é isso que faz a
 * temperatura subir e descer junto com a animação.
 */
import type { ReactNode } from 'react';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Fill from 'ol/style/Fill';
import Icon from 'ol/style/Icon';
import RegularShape from 'ol/style/RegularShape';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import Text from 'ol/style/Text';
import type { FeatureLike } from 'ol/Feature';
import { comSelo } from '../map/contraste';
import { criarCache, dpr, fatorDoZoom } from '../map/escalaDoZoom';
import { Z } from '../map/zIndex';
import { shownTimeOf, useTimeStore } from '../state/time';
import { METEO_ATRIBUICAO } from './api';
import { corDe, rotuloMapa } from './escalas';
import { DEFASADA_MS, idadeMs, kmh, leituraNoQuadro, valorDe } from './leitura';
import { useMeteoStore } from './store';
import type { Estacao, Estilo, Leitura, Rede } from './types';

/** O que o popup recebe ao clicar. */
export interface MeteoFeature {
  estacao: Estacao;
  /** Observação que vale no quadro exibido, ou `null`. */
  leitura: Leitura | null;
  /** A observação tem mais de 1 h no quadro (o símbolo esmaece). */
  defasada: boolean;
}

export const COR_SEM_DADO = '#8c99ab';
const CONTORNO = 'rgba(8,13,24,0.9)';
const HALO = 'rgba(10,16,28,0.85)';

/** "#rrggbb" + alfa → "rgba(...)". A opacidade entra na cor: `Style` não tem opacidade própria. */
export function comAlfa(hex: string, a: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1]!, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${Math.round(a * 1000) / 1000})`;
}

/** Meio-lado aproximado do símbolo em px (para o selo e o deslocamento do rótulo). */
export function raioDe(rede: Rede, tamanho: number): number {
  return rede === 'm' ? tamanho * 0.8 : tamanho * 0.72;
}

// ---------------------------------------------------------------------------
// Estilos em cache
// ---------------------------------------------------------------------------

// Com teto (`CacheLimitado`): as chaves têm cor, texto, rumo, opacidade e o
// tamanho já multiplicado pelo fator do zoom — cada parte é discreta, o
// produto numa sessão longa não.
const marcadores = criarCache<Style>('meteo.marcadores', 600);
const textos = criarCache<Style>('meteo.textos', 1500);
const setas = criarCache<Style>('meteo.setas', 1500);
const sprites = criarCache<HTMLCanvasElement>('meteo.sprites', 300);

function estiloMarcador(rede: Rede, cor: string | null, tamanho: number, opacidade: number): Style {
  const k = `${rede}|${cor ?? '-'}|${tamanho.toFixed(2)}|${opacidade.toFixed(3)}`;
  let s = marcadores.get(k);
  if (s) return s;
  const raio = raioDe(rede, tamanho);
  s = new Style({
    image: new RegularShape({
      points: 4,
      radius: rede === 'm' ? raio : raio * Math.SQRT2 * 0.98,
      // Quadrado (INMET) = losango girado 45°.
      angle: rede === 'm' ? 0 : Math.PI / 4,
      fill: cor ? new Fill({ color: comAlfa(cor, opacidade) }) : undefined,
      stroke: cor
        ? new Stroke({ color: CONTORNO, width: 1.3 })
        : new Stroke({ color: comAlfa(COR_SEM_DADO, opacidade), width: 1.2, lineDash: [2, 2] }),
      // Obstáculo: o símbolo nunca some, e os rótulos desviam dele.
      declutterMode: 'obstacle',
    }),
    zIndex: 2,
  });
  marcadores.set(k, s);
  return s;
}

/**
 * Corpo do rótulo em px. Acompanha o símbolo, mas devagar e sem descer de
 * 10 px: número menor que isso não se lê, e de longe o `declutter` já tira os
 * que colidiriam.
 */
export function fonteDoRotulo(fator: number): number {
  return Math.max(10, Math.min(13, Math.round(11 * Math.sqrt(fator))));
}

function estiloTexto(texto: string, cor: string, deslocamento: number, opacidade: number, fonte: number): Style {
  const k = `${texto}|${cor}|${deslocamento.toFixed(1)}|${opacidade.toFixed(3)}|${fonte}`;
  let s = textos.get(k);
  if (s) return s;
  s = new Style({
    text: new Text({
      text: texto,
      font: `600 ${fonte}px ui-sans-serif, system-ui, sans-serif`,
      fill: new Fill({ color: comAlfa(cor, opacidade) }),
      stroke: new Stroke({ color: HALO, width: 3 }),
      offsetX: deslocamento,
      textAlign: 'left',
      textBaseline: 'middle',
      declutterMode: 'declutter',
    }),
    zIndex: 3,
  });
  textos.set(k, s);
  return s;
}

/**
 * Comprimento da seta (px) pela velocidade: um degrau a cada 5 km/h, até 40.
 * A seta inteira acompanha o fator do zoom (`tamanho` é o do painel, sem ele).
 */
export function comprimentoSeta(vKmh: number, tamanho: number, fator = 1): number {
  const degrau = Math.min(8, Math.floor(vKmh / 5));
  return Math.round((tamanho + 5 + degrau * 2.2) * fator * 2) / 2;
}

/** Seta vertical (para o norte), com a base no centro do canvas de baixo. */
function spriteSeta(comprimento: number, opacidade: number, fator: number): HTMLCanvasElement | null {
  const r = dpr();
  const k = `${comprimento}|${opacidade.toFixed(3)}|${r}|${fator}`;
  const pronto = sprites.get(k);
  if (pronto) return pronto;
  if (typeof document === 'undefined') return null;
  // A cabeça acompanha o fator; a espessura do traço, não (é px de tela).
  const ponta = 1.2;
  const cabeca = 4.2 * fator;
  const aba = 3 * fator;
  // Largura: as abas mais a folga do traço (10 px no fator 1, como antes).
  const w = Math.max(10, Math.ceil(2 * (aba + 2)));
  const c = document.createElement('canvas');
  c.width = Math.ceil(w * r);
  c.height = Math.ceil(comprimento * r);
  const ctx = c.getContext('2d');
  if (!ctx) return null;
  ctx.scale(r, r);
  const x = w / 2;
  const desenhar = (cor: string, largura: number) => {
    ctx.strokeStyle = cor;
    ctx.lineWidth = largura;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(x, comprimento);
    ctx.lineTo(x, ponta);
    ctx.moveTo(x - aba, ponta + cabeca);
    ctx.lineTo(x, ponta);
    ctx.lineTo(x + aba, ponta + cabeca);
    ctx.stroke();
  };
  desenhar(`rgba(8,13,24,${0.75 * opacidade})`, 3.4);
  desenhar(`rgba(236,242,248,${opacidade})`, 1.4);
  sprites.set(k, c);
  return c;
}

function estiloSeta(direcaoDe: number, vKmh: number, tamanho: number, opacidade: number, fator: number): Style | null {
  const comprimento = comprimentoSeta(vKmh, tamanho, fator);
  // Para onde o vento VAI, em degraus de 5° (o cache não cresce sem limite).
  const para = (Math.round(((direcaoDe + 180) % 360) / 5) * 5) % 360;
  const k = `${comprimento}|${para}|${opacidade.toFixed(3)}|${dpr()}|${fator}`;
  const pronto = setas.get(k);
  if (pronto) return pronto;
  const img = spriteSeta(comprimento, opacidade, fator);
  if (!img) return null;
  const s = new Style({
    image: new Icon({
      img,
      width: img.width / dpr(),
      height: img.height / dpr(),
      anchor: [0.5, 1],
      rotation: (para * Math.PI) / 180,
      rotateWithView: true,
      declutterMode: 'none',
    }),
    zIndex: 1,
  });
  setas.set(k, s);
  return s;
}

/**
 * Estilos de uma estação no quadro (função pura sobre o estado, fora o cache).
 * `fator` é o do zoom (`map/escalaDoZoom.ts`): multiplica o símbolo, a seta e
 * o afastamento do rótulo.
 */
export function estilosDe(d: MeteoFeature, estilo: Estilo, fator = 1): Style[] {
  const { estacao, leitura, defasada } = d;
  if (estacao.rede === 'm' && !estilo.mostrarMetar) return [];
  const v = valorDe(leitura, estilo.variavel);
  const opac = estilo.opacidade * (v === null ? 0.6 : defasada ? 0.55 : 1);
  const cor = v === null ? null : corDe(estilo.variavel, v);
  const tamanho = estilo.tamanho * fator;
  const saida: Style[] = [estiloMarcador(estacao.rede, cor, tamanho, opac)];

  if (estilo.mostrarVento && leitura) {
    const vv = leitura.valores.vv;
    const dv = leitura.valores.dv;
    // Calmaria não tem direção: sem seta, e o número (na variável vento) diz 0.
    if (vv !== undefined && dv !== undefined && kmh(vv) >= 2) {
      const s = estiloSeta(dv, kmh(vv), estilo.tamanho, opac, fator);
      if (s) saida.push(s);
    }
  }
  if (v !== null && cor) {
    saida.push(estiloTexto(rotuloMapa(estilo.variavel, v), cor, raioDe(estacao.rede, tamanho) + 3, opac, fonteDoRotulo(fator)));
  }
  return saida;
}

/**
 * Estilos de uma estação na resolução do mapa: o fator do zoom e, sobre a foto
 * da foto de fundo, o selo escuro por baixo (map/contraste.ts). É o que a camada usa.
 */
export function estilosNoZoom(d: MeteoFeature, estilo: Estilo, resolution: number | undefined): Style[] {
  const fator = fatorDoZoom(resolution);
  return comSelo(estilosDe(d, estilo, fator), raioDe(d.estacao.rede, estilo.tamanho * fator) + 3, resolution);
}

export interface MeteoLayer {
  layer: VectorLayer<VectorSource>;
  dispose: () => void;
}

export function createMeteoLayer(renderPopup: (f: MeteoFeature) => ReactNode | null): MeteoLayer {
  const source = new VectorSource({ attributions: METEO_ATRIBUICAO });
  const layer = new VectorLayer({
    source,
    // Acima da máscara do filtro de focos (`AREA_FOCUS`) e das outras
    // estações pontuais: o número precisa ficar legível por cima delas.
    zIndex: Z.METEO,
    className: 'ol-layer amaview-meteo',
    properties: { id: 'meteo', title: 'Estações meteorológicas (INMET, METAR)' },
    declutter: true,
    updateWhileAnimating: false,
    updateWhileInteracting: false,
  });

  layer.setStyle((feature, resolution) => {
    const d = feature.get('meteo') as MeteoFeature | undefined;
    if (!d) return [];
    const { estilo } = useMeteoStore.getState();
    return estilosNoZoom(d, estilo, resolution);
  });

  layer.set('popup', (feature: FeatureLike): ReactNode | null => {
    const d = feature.get('meteo') as MeteoFeature | undefined;
    return d ? renderPopup(d) : null;
  });

  function reconstruir(): void {
    const { dados } = useMeteoStore.getState();
    source.clear(true);
    source.addFeatures(
      dados.estacoes.map((e) => {
        const f = new Feature({ geometry: new Point([e.mx, e.my]) });
        f.set('meteo', { estacao: e, leitura: null, defasada: false } satisfies MeteoFeature, true);
        return f;
      }),
    );
    reavaliar(true);
  }

  /**
   * Relê a observação de cada estação no quadro exibido. Só redesenha quando
   * alguma muda: o loop a 5 fps não pode repintar a camada sem nada novo.
   */
  function reavaliar(forcar = false): void {
    const { dados, visivel } = useMeteoStore.getState();
    layer.setVisible(visivel);
    if (!visivel) return;
    const instante = shownTimeOf(useTimeStore.getState());
    let mudou = forcar;
    for (const f of source.getFeatures()) {
      const d = f.get('meteo') as MeteoFeature;
      const l = leituraNoQuadro(d.estacao, dados, instante);
      // A mesma observação pode passar de 1 h de idade com o quadro andando:
      // o esmaecimento entra na comparação.
      const defasada = !!l && idadeMs(l, instante) > DEFASADA_MS;
      if (!forcar && (l?.ms ?? null) === (d.leitura?.ms ?? null) && defasada === d.defasada) continue;
      f.set('meteo', { estacao: d.estacao, leitura: l, defasada } satisfies MeteoFeature, true);
      mudou = true;
    }
    if (mudou) layer.changed();
  }

  const pararStore = useMeteoStore.subscribe((a, b) => {
    if (a.dados !== b.dados) reconstruir();
    else if (a.visivel !== b.visivel) reavaliar(true);
    else if (a.estilo !== b.estilo) layer.changed();
  });

  const pararTempo = useTimeStore.subscribe((a, b) => {
    if (a.displayedTime !== b.displayedTime || a.currentTime !== b.currentTime) reavaliar();
  });

  reconstruir();

  return {
    layer,
    dispose: () => {
      pararStore();
      pararTempo();
      source.clear(true);
    },
  };
}
